// Uniswap V2 Clone with PancakeSwap V2 Contracts
import "./config/reown"; // Reown AppKit (wallet + email/social connect)
import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence, useReducedMotion } from 'framer-motion';
import { Toaster } from 'react-hot-toast';
import { Layout, ErrorBoundary } from './components';
import { LanguageProvider } from './contexts/LanguageContext';
import { WalletProvider } from './contexts/WalletContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// ✅ Lazy load pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const CardDetail = lazy(() => import('./pages/CardDetail'));
const Gacha = lazy(() => import('./pages/Gacha'));
const BuyPack = lazy(() => import('./pages/BuyPack'));
const Checkout = lazy(() => import('./pages/Checkout'));
const UnlimitedPackQueue = lazy(() => import('./pages/UnlimitedPackQueue'));
const LimitedPackHistory = lazy(() => import('./pages/LimitedPackHistory'));
const Profile = lazy(() => import('./pages/Profile'));
const Shipping = lazy(() => import('./pages/Shipping'));
const Main = lazy(() => import('./pages/Main'));
const Referral = lazy(() => import('./pages/Referral'));
const LibraryDetails = lazy(() => import('./pages/LibraryDetails'));
const Library = lazy(() => import('./pages/Library'));

// Loading component
const PageLoader = () => (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full size-12 border-t-2 border-b-2 border-purple-500"></div>
    </div>
);

// Create a client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 60 * 1000, // 1 minute
            cacheTime: 5 * 60 * 1000, // 5 minutes
            retry: 1,
            refetchOnWindowFocus: false,
        },
    },
});

function AppContent() {
    const prefersReducedMotion = useReducedMotion();
    return (
        <div className="min-h-screen bg-gray-950">
            <Toaster position="top-right" />
            <Layout>
                <Suspense fallback={<PageLoader />}>
                    <AnimatePresence mode={prefersReducedMotion ? "sync" : "wait"} initial={!prefersReducedMotion}>
                        <Routes>
                            <Route path="/" element={<Navigate to="/main" replace />} />
                            <Route path="/main" element={<Main />} />
                            <Route path="/marketplace" element={<Marketplace />} />
                            <Route path="/marketplace/:cardId" element={<CardDetail />} />
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/gacha" element={<Gacha />} />
                            <Route path="/checkout" element={<Checkout />} />
                            <Route path="/unlimited-pack" element={<UnlimitedPackQueue />} />
                            <Route path="/limited-pack-history" element={<LimitedPackHistory />} />
                            <Route path="/pack-history" element={<LimitedPackHistory />} />
                            <Route path="/home" element={<BuyPack />} />
                            <Route path="/referral" element={<Referral />} />
                            <Route path="/profile" element={<Profile />} />
                            {/* <Route path="/shipping" element={<Shipping />} /> */}
                            <Route path="/vault/:cardId" element={<LibraryDetails />} />
                            <Route path="/vault" element={<Library />} />
                        </Routes>
                    </AnimatePresence>
                </Suspense>
            </Layout>
        </div>
    );
}

export default function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <ErrorBoundary>
                <Router>
                    <LanguageProvider>
                        <WalletProvider>
                            <AppContent />
                        </WalletProvider>
                    </LanguageProvider>
                </Router>
            </ErrorBoundary>
            {process.env.REACT_APP_ENV === 'staging' && <ReactQueryDevtools />}
        </QueryClientProvider>
    );
}
